import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Box,
  Icon,
  SimpleGrid,
  Center,
  Flex,
  useMediaQuery,
  Button,
} from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { EXPLORERS, USDC_NEW_DENOM } from '@/constants/constants'
import { Spinner } from '../../atoms/Spinner'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { AiOutlineCrown } from 'react-icons/ai'
import { IoGameControllerOutline } from 'react-icons/io5'
import { GetUserStatsDataQuery } from '@/__generated__/graphql'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { useEffect } from 'react'
import { getEthereumAddress } from '@ninjablaze/sdk-ts'
import { useTranslation } from 'next-i18next/pages'
import { GradientText } from '@/components/atoms/GradientText/GradientText'
import Image from 'next/image'
import { getNftRole } from '@/helpers/getNftRole'
import { getNftColor } from '@/helpers/getNftColor'
import { GET_USER_STATS_DATA } from '@/api/query'
import { tranformNftList } from '../../../helpers/tranformNftList'
import { nftsImages } from '../../../helpers/nftsImages'
import Link from 'next/link'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import SendTipIcon from '@/public/assets/icons/get-money.svg'
import UserIcon from '@/public/assets/icons/user.svg'
import { AppModals, useAppModal } from '@/context/ModalProvider'

const UserStatsModal = ({ onClose, address }: { onClose: () => void; address: string }) => {
  const { t } = useTranslation()

  const { data, refetch } = useQuery(GET_USER_STATS_DATA, {
    variables: { address },
  })

  useEffect(() => {
    refetch()
  }, [])

  return (
    <Modal size={'lg'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          {t('common.profile-stats')}
        </ModalHeader>
        <ModalCloseButton onClick={onClose} size="sm" />
        <ModalBody pb={5}>
          <UserModalContent data={data} address={address} />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const UserModalContent = ({ data, address }: { data?: GetUserStatsDataQuery; address: string }) => {
  const [isLargerThanMd] = useMediaQuery('(min-width: 768px)')
  const { t } = useTranslation()
  const numberOfNfts = data?.nfts?.totalCount
  const isNftOwner = Boolean(numberOfNfts)
  const nfts = tranformNftList(data?.nfts?.nodes || [])
  const modal = useAppModal()

  if (!data) {
    return (
      <Center>
        <Spinner />
      </Center>
    )
  }

  return (
    <>
      <Flex mb={4} flexDir={{ base: 'column', sm: 'row' }} alignItems={'center'} justifyContent={'space-between'}>
        <Flex
          mb={{ base: 3, sm: 0 }}
          alignItems={'center'}
          justifyContent={{ base: 'center', sm: 'start' }}
          gap={3}
          rounded={'xl'}
        >
          <AvatarCircle size={'lg'} offset={4} src={data?.user?.profileAvatar || ''} />
          <Flex flexDir={'column'} alignItems={'start'}>
            {isNftOwner ? (
              <GradientText
                numberOfNfts={numberOfNfts!}
                props={{
                  fontWeight: 700,
                  fontSize: 'xl',
                }}
              >
                {data?.user?.profileUserName}
              </GradientText>
            ) : (
              <Text fontWeight={700} fontSize={'xl'}>
                {data?.user?.profileUserName}
              </Text>
            )}
            {isNftOwner && (
              <Box
                rounded={'md'}
                wordBreak={'keep-all'}
                whiteSpace={'nowrap'}
                fontSize={'xs'}
                fontWeight={700}
                px={1}
                bg={getNftColor(numberOfNfts!)}
              >
                {getNftRole(numberOfNfts!)}
              </Box>
            )}
          </Flex>
        </Flex>
        {data.user?.created && (
          <StatsRow
            styles={{ mb: 0, textAlign: { base: 'left', sm: 'right' }, w: { base: 'full', sm: 'auto' } }}
            name={t('common.registration-date')}
            value={new Date(data.user?.created!).toLocaleString()}
          />
        )}
      </Flex>
      {data.user?.created && (
        <SimpleGrid columns={2} gap={3} mb={4}>
          <Box w={'full'}>
            <Link
              style={{
                display: 'block',
                width: '100%',
              }}
              href={'/activity/' + address}
            >
              <Button gap={1} color={'ninja.100'} w={'full'} variant={'secondary'} rounded={'xl'}>
                <Icon w={5} h={5} as={UserIcon} />
                {t('common.profile-details')}
              </Button>
            </Link>
          </Box>
          <Box w={'full'}>
            <Button
              onClick={() =>
                modal.openModalNew(AppModals.SendTip, {
                  receiver: { address, avatar: data.user?.profileAvatar!, username: data.user?.profileUserName! },
                })
              }
              gap={1}
              color={'ninja.100'}
              w={'full'}
              variant={'secondary'}
              rounded={'xl'}
            >
              <Icon w={5} h={5} as={SendTipIcon} />
              {t('common.send-tip')}
            </Button>
          </Box>
        </SimpleGrid>
      )}
      <Flex
        mb={3}
        flexDir={'column'}
        alignItems={'center'}
        rounded={'3xl'}
        border={'2px solid'}
        py={4}
        px={6}
        borderColor={'ninja.500'}
      >
        <Text fontSize={'sm'} color={'ninja.200'} fontWeight={700}>
          {t('common.total-win')}
        </Text>
        <FiatOrCryptoAmount
          value={new TokenMoney(data?.userStatisticsV2?.payoutAmount || '0', USDC_NEW_DENOM)}
          iconSize={5}
          wrapperStyles={{
            fontSize: 'xl',
          }}
        />
      </Flex>
      <SimpleGrid gap={3} columns={2}>
        <StatsRow
          name={t('common.games')}
          value={data.userStatisticsV2?.betCount.toString() || '0'}
          icon={<Icon boxSize={8} color={'ninja.300'} as={IoGameControllerOutline} />}
        />
        <StatsRow
          name={t('common.wins')}
          value={data.userStatisticsV2?.payoutCount.toString() || '0'}
          icon={<Icon boxSize={8} color={'ninja.300'} as={AiOutlineCrown} />}
        />
      </SimpleGrid>
      <StatsRow
        name={t('common.player-wallet')}
        value={
          <CopiableLink
            trimmedLength={24}
            maxLength={isLargerThanMd ? 48 : 32}
            value={getEthereumAddress(address)}
            link={`${EXPLORERS.INJECTIVE_WALLETS}${address}`}
          />
        }
      />
      {isNftOwner && (
        <Box>
          <Flex mb={4} justifyContent={'space-between'} alignItems={'center'}>
            <Text fontWeight={'700'} fontSize={'xl'}>
              {t('common.nfts')}
            </Text>
            {/* <ChakraLink href={'https://injective.talis.art/collection/65d214819b0824cc8e79d0d9'} target="_blank">
              <Button rounded={'xl'} h={10}>
                See Collection
              </Button>
            </ChakraLink> */}
          </Flex>
          <SimpleGrid columns={{ base: 3, md: 5 }} gap={3}>
            {nfts.map((nft, index) => {
              return (
                <Box key={index} pos={'relative'}>
                  <Flex
                    fontSize={'xs'}
                    w={6}
                    h={6}
                    fontWeight={700}
                    pos={'absolute'}
                    top={-2}
                    right={-2}
                    bg={'ninja.500'}
                    color={'ninja.100'}
                    rounded={'full'}
                    justifyContent={'center'}
                    alignItems={'center'}
                  >
                    {nft.amount}
                  </Flex>
                  <Image src={nftsImages[nft.type as keyof typeof nftsImages]} alt={''} />
                </Box>
              )
            })}
          </SimpleGrid>
        </Box>
      )}
    </>
  )
}

export default UserStatsModal
